import React, { useCallback } from 'react';
import { Modal, ModalVariant, Button, Grid, GridItem, FormGroup, TextInput, Form, Card, CardTitle, CardBody, Alert, Dropdown, DropdownToggle, DropdownToggleAction, DropdownItem } from '@patternfly/react-core';

import './customization-page.css';
import { copyRoiFmaIds, copyRoiFmaIdsOutput, hideFullRoiDetails, showFullRoiDetails, StoreState, updateRoiFmaId, updateRoiPhysicalProperty } from '../../store/reducers';
import { useDispatch, useSelector } from 'react-redux';
import { selectCustomizationValidationError, selectGlobalRoiCustomizationById, selectRoiCustomizationById } from '../../store/selectors';
import RoiItem from './RoiItem';
import { TableComposable, Tbody, Td, Thead, Tr } from '@patternfly/react-table';
import { columnIds, columnLangKeys } from './RoiTable';
import { get } from "lodash-es";
import { useTranslation } from 'react-i18next';
import PhysicalPropertiesDropdown from './PhysicalPropertiesDropdown';
import { CustomizationObjectType, GlobalRoiCustomization, PhysicalProperty, RoiCustomization } from '../../store/model-customization-types';
import ConfirmationModal from '../../components/confirmation-modal';
import { extractSharedFmaIdAttributes } from '../../store/model-customization-helpers';

/** Models a full list of ROI IDs that the details modal can move through, and the current position (cursor) in it. */
export type FullRoiDetailsId = {
    currentRoiId: string;
    roiIdList: string[];
    isGlobalRoi: boolean;
}


const GRID_SPAN = 4;

const enum FmaCodeCopyTarget {
    'AllRois' = 'all-rois', 
    'WithinOutput' = 'within-output',
}

const FullRoiDetailsModal = () => {
    const [isCopyFmaIdsConfirmationModalOpen, setCopyFmaIdsConfirmationModalOpen] = React.useState(false);
    const [confirmationModalKey, setConfirmationModalKey] = React.useState(FmaCodeCopyTarget.AllRois);
    const [isCopyFmaIdsSelectOpen, setCopyFmaIdsSelectOpen] = React.useState(false);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const fullRoiDetails = useSelector((state: StoreState) => state.fullRoiDetails);
    const isGlobalRoi = fullRoiDetails !== undefined && fullRoiDetails.isGlobalRoi === true;
    const validationError = useSelector((state: StoreState) => fullRoiDetails ? selectCustomizationValidationError(state, fullRoiDetails.currentRoiId) : undefined);

    const roi = useSelector((state: StoreState): GlobalRoiCustomization | RoiCustomization | undefined => {
        if (!fullRoiDetails) {
            return undefined;
        }
        return isGlobalRoi
            ? selectGlobalRoiCustomizationById(state, fullRoiDetails.currentRoiId)
            : selectRoiCustomizationById(state, fullRoiDetails.currentRoiId);
    });

    const handleClose = useCallback(() => {
        dispatch(hideFullRoiDetails());
    }, [dispatch]);

    const handlePreviousRoi = useCallback(() => {
        if (fullRoiDetails) {
            const { currentRoiId, roiIdList } = fullRoiDetails;
            const currentRoiListIndex = roiIdList.indexOf(currentRoiId);
            if (currentRoiListIndex > 0) {
                dispatch(showFullRoiDetails({ currentRoiId: roiIdList[currentRoiListIndex - 1], roiIdList, isGlobalRoi }));
            }
        }
    }, [dispatch, fullRoiDetails, isGlobalRoi]);

    const handleNextRoi = useCallback(() => {
        if (fullRoiDetails) {
            const { currentRoiId, roiIdList } = fullRoiDetails;
            const currentRoiListIndex = roiIdList.indexOf(currentRoiId);
            if (currentRoiListIndex < roiIdList.length - 1) {
                dispatch(showFullRoiDetails({ currentRoiId: roiIdList[currentRoiListIndex + 1], roiIdList, isGlobalRoi }));
            }
        }
    }, [dispatch, fullRoiDetails, isGlobalRoi]);

    const handleFmaCodeInputChange = useCallback((text, event) => {
        if (roi) {
            dispatch(updateRoiFmaId(roi.id, { hasFmaId: true, [event.target.id]: text }, isGlobalRoi));
        }
    }, [dispatch, isGlobalRoi, roi]);

    const handlePhysicalPropertyAttributeChanged = useCallback((value: PhysicalProperty) => {
        if (roi) { dispatch(updateRoiPhysicalProperty(roi.id, value, undefined, isGlobalRoi)); }
    }, [dispatch, isGlobalRoi, roi]);


    const isOpen = fullRoiDetails !== undefined && roi !== undefined;

    const currentIndex = fullRoiDetails ? fullRoiDetails.roiIdList.indexOf(fullRoiDetails.currentRoiId) : -1;
    const hasNext = fullRoiDetails && fullRoiDetails.roiIdList[fullRoiDetails.roiIdList.length - 1] !== fullRoiDetails.currentRoiId;
    const hasPrevious = fullRoiDetails && fullRoiDetails.roiIdList[0] !== fullRoiDetails.currentRoiId;

    const coveredRoisLength = isGlobalRoi ? get(roi, 'coveredRois', ['']).length : -1;

    const hasPhysicalPropertiesValidationError = validationError && validationError.type === CustomizationObjectType.PhysicalProperties;

    const handleCopyFmaValues = (fmaId: any) => {
        dispatch(copyRoiFmaIds(fmaId));
    }

    const handleCopyFmaValuesToOutput = (fmaId: any) => {
        dispatch(copyRoiFmaIdsOutput(fmaId));
    }

    const onCopyFmaCodesClick = (type: FmaCodeCopyTarget) => {
        if (roi) {
            if (type === FmaCodeCopyTarget.AllRois) {
                handleCopyFmaValues(extractSharedFmaIdAttributes(roi, false));
            } else if (type === FmaCodeCopyTarget.WithinOutput) {
                handleCopyFmaValuesToOutput(extractSharedFmaIdAttributes(roi, true));
            }
        }
    };

    const handleOpenCopyFmaIdsConfirmationModal = (key: FmaCodeCopyTarget) => {
        setConfirmationModalKey(key);
        // Set the modal open state
        setCopyFmaIdsConfirmationModalOpen(true);
    }

    const onCopyFmaIdsSelectToggle = (isOpen: boolean) => {
        setCopyFmaIdsSelectOpen(isOpen);
    }

    const onActionSelect = () => {
        setCopyFmaIdsSelectOpen(false);
    };


    return (
        <Modal
            className="full-roi-details-modal"
            tabIndex={0}
            isOpen={isOpen}
            onClose={handleClose}
            title={isGlobalRoi ? t('fullRoiDetails.title.global', { count: coveredRoisLength }) : t('fullRoiDetails.title.regular')}
            variant={ModalVariant.large}
            actions={[
                <Button key="prev" variant="secondary" onClick={handlePreviousRoi} isDisabled={!hasPrevious}>{t('fullRoiDetails.previous')}</Button>,
                <Button key="next" variant="secondary" onClick={handleNextRoi} isDisabled={!hasNext}>{t('fullRoiDetails.next')}</Button>,
                <span className="list-index">{currentIndex !== -1 && fullRoiDetails && (<span>{currentIndex + 1}/{fullRoiDetails.roiIdList.length}</span>)}</span>,
                <Button key="close" variant="secondary" onClick={handleClose}>{t('common.close')}</Button>,
            ]}
        >
            {isCopyFmaIdsConfirmationModalOpen && (
                <ConfirmationModal
                    isOpen={isCopyFmaIdsConfirmationModalOpen}
                    title={confirmationModalKey === FmaCodeCopyTarget.AllRois ? t('fullRoiDetails.copyFmaIdsInUserConfigConfirm.title') : t('fullRoiDetails.copyFmaIdsInOutputConfigConfirm.title')}
                    description={confirmationModalKey === FmaCodeCopyTarget.AllRois ? t('fullRoiDetails.copyFmaIdsInUserConfigConfirm.message') : t('fullRoiDetails.copyFmaIdsInOutputConfigConfirm.message')}
                    onConfirm={() => {
                        onCopyFmaCodesClick(confirmationModalKey);
                        setCopyFmaIdsConfirmationModalOpen(false);
                    }}
                    onClose={() => setCopyFmaIdsConfirmationModalOpen(false)}
                    variant={ModalVariant.small}
                />
            )}
            {roi && (
                <div>
                    <TableComposable variant='compact'>
                        <Thead>
                            <Tr>
                                <Td></Td>
                                <Td>{t(columnLangKeys[columnIds.name])}</Td>
                                <Td>{t(columnLangKeys[columnIds.isIncluded])}</Td>
                                <Td>{t(columnLangKeys[columnIds.color])}</Td>
                                <Td>{t(columnLangKeys[columnIds.operation])}</Td>
                                <Td>{t(columnLangKeys[columnIds.interpretedType])}</Td>
                            </Tr>
                        </Thead>
                        <Tbody>
                            <RoiItem roiId={roi.id} hideIdentificationCodeFields isGlobalRoi={isGlobalRoi} />
                        </Tbody>
                    </TableComposable>

                    <div className="identification-code-table">
                        <Form isHorizontal>
                            <Grid className='fma-grid-gutter'>
                                <GridItem span={8} className='fma-copy-non-highlight'>
                                    <GridItem span={GRID_SPAN}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-1"
                                            label={t('fullRoiDetails.codingScheme.codeValue')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdCodeValue"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdCodeValue || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                    <GridItem span={GRID_SPAN}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-2"
                                            label={t('fullRoiDetails.codingScheme.codeMeaning')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdCodeMeaning"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdCodeMeaning || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                </GridItem>
                                <Grid className='fma-copy-highlight fma-grid-gutter'>
                                    <GridItem span={GRID_SPAN}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-3"
                                            label={t('fullRoiDetails.codingScheme.codeSchemeDesignator')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdCodingSchemeDesignator"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdCodingSchemeDesignator || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                    <GridItem span={GRID_SPAN}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-4"
                                            label={t('fullRoiDetails.codingScheme.codeSchemeVersion')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdCodingSchemeVersion"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdCodingSchemeVersion || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                    <GridItem span={GRID_SPAN}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-5"
                                            label={t('fullRoiDetails.codingScheme.mappingResource')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdMappingResource"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdMappingResource || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                    <GridItem span={GRID_SPAN}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-6"
                                            label={t('fullRoiDetails.codingScheme.contextGroupVersion')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdContextGroupVersion"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdContextGroupVersion || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                    <GridItem span={GRID_SPAN}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-7"
                                            label={t('fullRoiDetails.codingScheme.contextIdentifier')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdContextIdentifier"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdContextIdentifier || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                    <GridItem span={GRID_SPAN}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-8"
                                            label={t('fullRoiDetails.codingScheme.contextUid')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdContextUid"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdContextUid || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                    <GridItem span={GRID_SPAN}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-9"
                                            label={t('fullRoiDetails.codingScheme.mappingResourceUid')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdMappingResourceUid"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdMappingResourceUid || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                    <GridItem span={5}>
                                        <FormGroup
                                            fieldId="field-roi-details-fmaid-10"
                                            label={t('fullRoiDetails.codingScheme.mappingResourceName')}>
                                            <TextInput
                                                type="text"
                                                id="fmaIdMappingResourceName"
                                                onChange={handleFmaCodeInputChange}
                                                value={roi.fmaIdMappingResourceName || ''}
                                            />
                                        </FormGroup>
                                    </GridItem>
                                    <GridItem span={5}>
                                        {isGlobalRoi ? (
                                            <Button
                                                onClick={() => handleOpenCopyFmaIdsConfirmationModal(FmaCodeCopyTarget.AllRois)}
                                                variant="primary"
                                            >
                                                {t('fullRoiDetails.copyFmaIds.allRois')}
                                            </Button>
                                        ) : (
                                            <Dropdown
                                                onSelect={onActionSelect}
                                                toggle={
                                                    <DropdownToggle
                                                        id="toggle-split-button-action-primary"
                                                        splitButtonItems={[
                                                            <DropdownToggleAction
                                                                key="default-copy-action"
                                                                aria-label="copy-fma-ids-default"
                                                                onClick={(event) => {
                                                                    event.preventDefault(); // Prevent default action
                                                                    handleOpenCopyFmaIdsConfirmationModal(FmaCodeCopyTarget.AllRois);
                                                                }}
                                                            >
                                                                {t('fullRoiDetails.copyFmaIds.allRois')}
                                                            </DropdownToggleAction>

                                                        ]}
                                                        toggleVariant="primary"
                                                        splitButtonVariant="action"
                                                        onToggle={onCopyFmaIdsSelectToggle}
                                                    />
                                                }
                                                isOpen={isCopyFmaIdsSelectOpen}
                                                dropdownItems={[
                                                    <DropdownItem key={FmaCodeCopyTarget.AllRois} component="button" onClick={() => handleOpenCopyFmaIdsConfirmationModal(FmaCodeCopyTarget.AllRois)}>
                                                        {t('fullRoiDetails.copyFmaIds.allRois')}
                                                    </DropdownItem>,
                                                    <DropdownItem key={FmaCodeCopyTarget.WithinOutput} component="button" onClick={() => handleOpenCopyFmaIdsConfirmationModal(FmaCodeCopyTarget.WithinOutput)}>
                                                        {t('fullRoiDetails.copyFmaIds.withinOutput')}
                                                    </DropdownItem>
                                                ]}
                                            />
                                        )}
                                    </GridItem>
                                </Grid>
                            </Grid>
                            <Card isFlat className="physical-properties-card-container">
                                <CardTitle>{t('fullRoiDetails.physicalProperties.box.title')}</CardTitle>
                                <CardBody className="physical-properties-card">
                                    <FormGroup label={t('fullRoiDetails.physicalProperties.box.label')} fieldId="field-roi-physical-property-attribute">
                                        <PhysicalPropertiesDropdown
                                            id="physicalPropertyAttribute"
                                            onSelect={handlePhysicalPropertyAttributeChanged}
                                            value={roi.physicalPropertyAttribute || ''} />
                                    </FormGroup>
                                    <FormGroup
                                        label={t('fullRoiDetails.physicalProperties.box.value')}
                                        fieldId="field-roi-physical-property-value">
                                        <TextInput
                                            type="text"
                                            id="physicalPropertyValue"
                                            onChange={handleFmaCodeInputChange}
                                            value={roi.physicalPropertyValue || ''}
                                        />
                                    </FormGroup>
                                    {hasPhysicalPropertiesValidationError &&
                                        (<div className="roi-item validation-error validation-error-box">
                                            <Alert variant="danger" isInline isPlain isExpandable title={`Validation error: ${validationError.message}`}>
                                                {validationError.field && (<div>Field: {validationError.field}</div>)}
                                                <div>Error type: {validationError.detail}</div>
                                                <div>Error ctx: {validationError.ctx}</div>
                                            </Alert>
                                        </div>)}
                                </CardBody>

                            </Card>

                        </Form>
                    </div>

                </div>
            )}
        </Modal>
    );
}

export default FullRoiDetailsModal;
